// Writes the demo catalog and a week of sales into the local databases
// before the app starts, using the same documents the app itself stores.
import PouchDB from "pouchdb-browser";
import uuidv1 from "uuid/v1";
import DeviceInfo from "../shims/device-info";
import * as demo from "./data";

const FLAG = "@DevPOS:demoData";
const DATABASES = [
  "attendants",
  "roles",
  "categories",
  "items",
  "discounts",
  "customers",
  "receipts",
  "payments",
  "shifts",
  "shiftreports",
  "taxes",
  "trash",
  "sessions",
  "printers",
  "company",
  "bluetoothscanner",
  "sync",
];

const VERSION = "2";
const open = name => new PouchDB(`${name}.db`);
const imagePath = item => (item.image ? `/items/${item.image}?v=1` : "");
const id = collection => `${collection}/${uuidv1()}`;
const money = value => Math.round(value * 100) / 100;
const startOfDay = time => Date.parse(new Date(time).toDateString());

// small deterministic generator so every demo looks the same
function randomizer(seed) {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

async function isEmpty(name) {
  const info = await open(name).info();
  return info.doc_count === 0;
}

async function destroyAll() {
  for (const name of DATABASES) {
    try {
      await open(name).destroy();
    } catch (e) {
      // database did not exist
    }
  }
  try {
    Object.keys(window.localStorage)
      .filter(key => key.startsWith("@Settings") || key === FLAG)
      .forEach(key => window.localStorage.removeItem(key));
  } catch (e) {
    // storage not available
  }
}

function catalog(now) {
  const stamp = { dateUpdated: now, syncStatus: false };

  const roles = demo.roles.map(role => ({ _id: id("Role"), role, ...stamp }));
  const attendants = demo.attendants.map(attendant => ({
    _id: id("Attendant"),
    commission: 0,
    ...attendant,
    ...stamp,
  }));
  const customers = [
    {
      name: "Default customer",
      email: "walkin@devpos.app",
      phoneNumber: "0000000000",
      note: "Walk-in customer",
    },
    ...demo.customers,
  ].map(customer => ({ _id: id("Customer"), ...customer, ...stamp }));
  const discounts = demo.discounts.map(discount => ({
    _id: id("Discount"),
    ...discount,
    ...stamp,
  }));
  const categories = demo.categories.map(category => ({
    _id: id("Category"),
    name: category.name,
    colorAndShape: JSON.stringify([{ color: category.color, shape: "square" }]),
    ...stamp,
  }));
  const items = demo.items.map(item => {
    const category = categories.find(c => c.name === item.category);
    const color = demo.categories.find(c => c.name === item.category).color;
    return {
      _id: id("Item"),
      name: item.name,
      description: item.name,
      soldBy: "Each",
      price: item.price,
      tax: 0,
      sku: item.sku,
      barcode: item.barcode,
      colorAndShape: JSON.stringify([{ color, shape: item.shape || "square" }]),
      colorOrImage: "",
      imagePath: imagePath(item),
      favorite: item.favorite ? "true" : "",
      category: category._id,
      taxes: JSON.stringify("[]"),
      ...stamp,
    };
  });

  return { roles, attendants, customers, discounts, categories, items };
}

function pickItem(random, total) {
  let target = random() * total;
  for (const item of demo.items) {
    target -= item.popularity;
    if (target <= 0) {
      return item;
    }
  }
  return demo.items[0];
}

function sales(now, data, deviceId) {
  const random = randomizer(20260929);
  const popularity = demo.items.reduce((sum, item) => sum + item.popularity, 0);
  // shift reports are listed per attendant, the owner gets most of them
  const names = demo.attendants.map(attendant => attendant.user_name);
  const rota = [0, 1, 0, 2, 0, 1, 0].map(index => names[index % names.length]);
  const walkIn = data.customers[0];
  const loyalty = data.discounts[0];
  const paymentTypes = ["Cash", "Cash", "Cash", "Card", "Visa", "Cash", "Amex"];

  const receipts = [];
  const payments = [];
  const shifts = [];
  const reports = [];
  let receiptNumber = 0;

  const hour = 3600000;
  const today = startOfDay(now);

  for (let day = demo.history.days; day >= 0; day -= 1) {
    const isToday = day === 0;
    const dayStart = startOfDay(today - day * 24 * hour + 12 * hour);
    const opened = isToday
      ? Math.max(dayStart, now - 3 * hour)
      : dayStart + 8 * hour;
    const closed = isToday ? now : dayStart + 18 * hour;
    const [min, max] = demo.history.receiptsPerDay;
    const count = isToday
      ? demo.history.receiptsToday
      : min + Math.floor(random() * (max - min + 1));
    const attendant = isToday
      ? names[0]
      : rota[(demo.history.days - day) % rota.length];
    const shiftId = id("Shift");

    const categoryTotals = {};
    const paymentTotals = {};
    let totalSales = 0;
    let totalDiscounts = 0;
    let cashSales = 0;

    for (let n = 0; n < count; n += 1) {
      const soldAt = Math.floor(
        opened + (closed - opened) * (n + random() * 0.8) / count,
      );
      const lines = [];
      const lineCount = 1 + Math.floor(random() * 3.4);
      for (let l = 0; l < lineCount; l += 1) {
        const item = pickItem(random, popularity);
        const existing = lines.find(line => line.item_name === item.name);
        if (existing) {
          existing.qty += 1;
        } else {
          lines.push({
            _id: id("ReceiptLine"),
            item: item.name,
            item_name: item.name,
            sold_by: "Each",
            category: item.category,
            price: item.price,
            qty: random() < 0.25 ? 2 : 1,
            commission_details: "[]",
            discount_rate: 0,
            tax: 0,
            discountType: "percentage",
          });
        }
      }

      const subtotal = money(
        lines.reduce((sum, line) => sum + line.price * line.qty, 0),
      );
      const discounted = random() < 0.15;
      const discountAmount = discounted
        ? money(subtotal * (loyalty.value / 100))
        : 0;
      const total = money(subtotal - discountAmount);
      const customer =
        random() < 0.3
          ? data.customers[
              1 + Math.floor(random() * (data.customers.length - 1))
            ]
          : walkIn;
      const type = paymentTypes[Math.floor(random() * paymentTypes.length)];
      const paid = type === "Cash" ? Math.ceil(total / 5) * 5 : total;
      receiptNumber += 1;
      const receipt = id("Receipt");

      receipts.push({
        _id: receipt,
        date: startOfDay(soldAt),
        status: "completed",
        reason: "",
        customer: customer._id,
        lines,
        discountName: discounted ? loyalty.name : "",
        discount: discounted ? loyalty._id : "",
        discountValue: discounted ? loyalty.value / 100 : 0,
        receiptNumber,
        discountType: "percentage",
        taxesValue: "0",
        taxesAmount: 0,
        shift: shiftId,
        deviceId,
        dateUpdated: soldAt,
        syncStatus: false,
        roundOff: false,
        attendant,
        orderType: "None",
      });
      payments.push({
        _id: id("Payment"),
        date: soldAt,
        receipt,
        paid,
        type: JSON.stringify([{ type, amount: total }]),
        deviceId,
        dateUpdated: soldAt,
        syncStatus: false,
      });

      lines.forEach(line => {
        categoryTotals[line.category] = money(
          (categoryTotals[line.category] || 0) + line.price * line.qty,
        );
      });
      paymentTotals[type] = money((paymentTotals[type] || 0) + total);
      totalSales = money(totalSales + total);
      totalDiscounts = money(totalDiscounts + discountAmount);
      if (type === "Cash") {
        cashSales = money(cashSales + total);
      }
    }

    const endingCash = money(demo.history.beginningCash + cashSales);
    shifts.push({
      _id: shiftId,
      beginning_cash: demo.history.beginningCash,
      ending_cash: endingCash,
      short: isToday ? null : 0,
      actual_money: isToday ? 0 : endingCash,
      total_sales: totalSales,
      total_discounts: totalDiscounts,
      total_taxes: 0,
      shift_beginning: opened,
      shift_end: isToday ? null : closed,
      attendant,
      status: isToday ? "Opened" : "Closed",
      numberOfTransaction: count,
      commissions: 0,
      pays: [],
      orderType: [],
      reportType: "XReading",
      dateUpdated: closed,
      syncStatus: false,
      categories_total_amounts: JSON.stringify(
        Object.keys(categoryTotals).map(name => ({
          name,
          total_amount: categoryTotals[name],
        })),
      ),
      mop_total_amounts: JSON.stringify(
        Object.keys(paymentTotals).map(name => ({
          name,
          total_amount: paymentTotals[name],
        })),
      ),
      voided: 0,
      cancelled: 0,
    });
    reports.push({
      _id: id("ShiftReports"),
      date: opened,
      shift: shiftId,
      status: isToday ? "Opened" : "Closed",
      shiftNumber: demo.history.days - day + 1,
      attendant,
    });
  }

  // the open receipt on the sales screen. The app counts it when it works
  // out receipt numbers, so it has to exist from the start.
  receipts.push({
    _id: id("Receipt"),
    date: today,
    status: "current",
    reason: "",
    customer: walkIn._id,
    lines: [],
    discountName: "",
    discount: "",
    discountValue: 0,
    receiptNumber: receiptNumber + 1,
    discountType: "percentage",
    taxesValue: demo.company.tax,
    taxesAmount: 0,
    shift: "",
    deviceId,
    dateUpdated: now,
    syncStatus: false,
    roundOff: false,
    attendant: "",
    orderType: "None",
  });

  return { receipts, payments, shifts, reports };
}

async function write() {
  const now = Date.now();
  const deviceId = DeviceInfo.getDeviceId();
  const data = catalog(now);
  const history = sales(now, data, deviceId);

  await open("company").bulkDocs([
    {
      _id: id("Company"),
      changeNoReceipts: "1",
      currencyDisable: false,
      enableOverallTax: false,
      smallSizeIcon: false,
      mediumSizeIcon: false,
      largeSizeIcon: true,
      hideCategory: false,
      multipleMop: false,
      hideMenuBar: false,
      allowRoundOff: false,
      ...demo.company,
    },
  ]);
  await open("printers").bulkDocs([{ _id: id("Printer"), ...demo.printer }]);
  await open("roles").bulkDocs(data.roles);
  await open("attendants").bulkDocs(data.attendants);
  await open("customers").bulkDocs(data.customers);
  await open("discounts").bulkDocs(data.discounts);
  await open("categories").bulkDocs(data.categories);
  await open("items").bulkDocs(data.items);
  await open("receipts").bulkDocs(history.receipts);
  await open("payments").bulkDocs(history.payments);
  await open("shifts").bulkDocs(history.shifts);
  await open("shiftreports").bulkDocs(history.reports);

  return {
    items: data.items.length,
    receipts: history.receipts.length - 1,
  };
}

// Browsers that received the demo data before it had photos get the photos
// added to the demo items they still have. Nothing else is changed.
async function addPhotos() {
  const db = open("items");
  const result = await db.allDocs({ include_docs: true });
  const changed = [];
  result.rows.forEach(row => {
    const match = demo.items.find(item => item.name === row.doc.name);
    if (match && match.image && !row.doc.imagePath) {
      changed.push(Object.assign({}, row.doc, { imagePath: imagePath(match) }));
    }
  });
  if (changed.length) {
    await db.bulkDocs(changed);
  }
  return changed.length;
}

export const demoLogins = demo.attendants.map(attendant => ({
  name: attendant.user_name,
  role: attendant.role,
  pin: attendant.pin_code,
}));

export async function seedDemoData() {
  const params = new URLSearchParams(window.location.search);
  const reset =
    params.get("demo") === "reset" &&
    window.confirm(
      "Reset DevPOS on this browser?\n\nAll local data will be deleted and replaced with the demo data.",
    );

  if (params.has("demo")) {
    if (reset) {
      await destroyAll();
    }
    params.delete("demo");
    const query = params.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (query ? `?${query}` : ""),
    );
  }

  let version = null;
  try {
    version = window.localStorage.getItem(FLAG);
  } catch (e) {
    version = null;
  }
  let seeded = !!version;
  const remember = () => {
    try {
      window.localStorage.setItem(FLAG, VERSION);
    } catch (e) {
      // storage not available
    }
  };

  // A browser gets the demo data once. One that was used before the demo
  // data existed only holds test entries, those are replaced. After that
  // the data of the browser is left alone unless a reset is requested.
  const fresh = (await isEmpty("attendants")) && (await isEmpty("items"));
  if (reset || !seeded || fresh) {
    if (!fresh && !reset) {
      await destroyAll();
    }
    const result = await write();
    remember();
    console.log(
      `DevPOS demo data loaded: ${result.items} items, ${
        result.receipts
      } receipts`,
    );
    seeded = true;
  } else if (version !== VERSION) {
    const photos = await addPhotos();
    remember();
    console.log(`DevPOS demo data updated: ${photos} item photos added`);
  }

  window.__DEVPOS_DEMO__ = seeded ? { logins: demoLogins } : null;
}
